import './App.css';
import * as React from "react";
import {useContext} from "react";
import Box from "@mui/material/Box";
import {CircularProgress} from "@mui/material";
import {BrowserRouter as Router, Navigate, Route, Routes, useLocation} from "react-router-dom";
import {CookiesProvider} from "react-cookie";
import {TokenContext, TokenProvider} from "./Context/TokenContext";
import {AuthorizationsContext, AuthorizationsProvider} from "./Context/AuthorizationsContext";
import {AUTHORIZATIONS} from "./Utils/authorizations";
import {Authentication} from "./Componant/Authentication/Authentication";
import QrGeneratorPage from "./Page/QrGeneratorPage";
import DashboardPage from "./Page/DashboardPage";
import MyQrCodesPage from "./Page/MyQrCodesPage";
import QrCodeAnalyticsPage from "./Page/QrCodeAnalyticsPage";
import PalettesPage from "./Page/PalettesPage";
import PaletteFormPage from "./Page/PaletteFormPage";
import DomainsPage from "./Page/DomainsPage";
import ApiKeysPage from "./Page/ApiKeysPage";
import ApiDocsPage from "./Page/ApiDocsPage";

function LoadingScreen() {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
            <CircularProgress/>
        </Box>
    );
}

function ProtectedRoute({children}) {
    const {isAuthenticated, isLoading} = useContext(TokenContext);
    const location = useLocation();

    if (isLoading) return <LoadingScreen/>;

    if (!isAuthenticated) {
        return <Navigate to="/authentication/callback" replace state={{from: location}}/>;
    }

    return children;
}

// À poser à l'intérieur d'un ProtectedRoute : redirige vers /create si le compte
// n'a pas la licence requise (l'appel /api/account a de toute façon le dernier mot
// côté serveur, ceci n'est qu'un confort de navigation).
function AuthorizedRoute({authorization, children}) {
    const {hasAuthorization, loading} = useContext(AuthorizationsContext);

    if (loading) return <LoadingScreen/>;
    if (!hasAuthorization(authorization)) return <Navigate to="/create" replace/>;

    return children;
}

function Landing() {
    const {hasAuthorization, loading} = useContext(AuthorizationsContext);

    if (loading) return <LoadingScreen/>;

    return <Navigate to={hasAuthorization(AUTHORIZATIONS.DASHBOARD) ? '/dashboard' : '/create'} replace/>;
}

function AppContent() {
    return (
        <Routes>
            <Route path="/authentication/callback" element={<Authentication/>}/>
            <Route path="/create" element={
                <ProtectedRoute><QrGeneratorPage/></ProtectedRoute>
            }/>
            <Route path="/dashboard" element={
                <ProtectedRoute>
                    <AuthorizedRoute authorization={AUTHORIZATIONS.DASHBOARD}><DashboardPage/></AuthorizedRoute>
                </ProtectedRoute>
            }/>
            <Route path="/my-qrcodes/:id" element={
                <ProtectedRoute>
                    <AuthorizedRoute authorization={AUTHORIZATIONS.TRACKED_QRCODES}><QrCodeAnalyticsPage/></AuthorizedRoute>
                </ProtectedRoute>
            }/>
            <Route path="/my-qrcodes" element={
                <ProtectedRoute>
                    <AuthorizedRoute authorization={AUTHORIZATIONS.TRACKED_QRCODES}><MyQrCodesPage/></AuthorizedRoute>
                </ProtectedRoute>
            }/>
            <Route path="/palettes/new" element={
                <ProtectedRoute>
                    <AuthorizedRoute authorization={AUTHORIZATIONS.PALETTES}><PaletteFormPage/></AuthorizedRoute>
                </ProtectedRoute>
            }/>
            <Route path="/palettes/:id" element={
                <ProtectedRoute>
                    <AuthorizedRoute authorization={AUTHORIZATIONS.PALETTES}><PaletteFormPage/></AuthorizedRoute>
                </ProtectedRoute>
            }/>
            <Route path="/palettes" element={
                <ProtectedRoute>
                    <AuthorizedRoute authorization={AUTHORIZATIONS.PALETTES}><PalettesPage/></AuthorizedRoute>
                </ProtectedRoute>
            }/>
            <Route path="/domains" element={
                <ProtectedRoute>
                    <AuthorizedRoute authorization={AUTHORIZATIONS.CUSTOM_DOMAINS}><DomainsPage/></AuthorizedRoute>
                </ProtectedRoute>
            }/>
            <Route path="/api-keys" element={
                <ProtectedRoute>
                    <AuthorizedRoute authorization={AUTHORIZATIONS.API_ACCESS}><ApiKeysPage/></AuthorizedRoute>
                </ProtectedRoute>
            }/>
            <Route path="/api-docs" element={
                <ProtectedRoute>
                    <AuthorizedRoute authorization={AUTHORIZATIONS.API_ACCESS}><ApiDocsPage/></AuthorizedRoute>
                </ProtectedRoute>
            }/>
            <Route path="/*" element={
                <ProtectedRoute><Landing/></ProtectedRoute>
            }/>
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <CookiesProvider>
                <Router>
                    <TokenProvider>
                        <AuthorizationsProvider>
                            <AppContent/>
                        </AuthorizationsProvider>
                    </TokenProvider>
                </Router>
            </CookiesProvider>
        </div>
    );
}

export default App;
